import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { cloneLibraryGame, getLibraryGames, PAGE_SIZE } from '../api/gameApiService'
import { LibrarySource } from '../api/gameTypes'
import { parseLibraryGamesResponseToGameCards } from '../api/typeConverters'
import { PaginationSelectOption } from '../composites/GamesOverview/components/PaginationControls'
import { useGameNotifications } from '../hooks/useGameNotifications'
import { TGameCard } from '../types/commonTypes'
import { areObjectsEqual, noop } from '../util/functional'
import { GameDataProviderInterface, GameFilters, useGameData as useOwnGameData } from './OwnGamesContextProvider'
import { UserContext, UserContextType } from './userContext'

// TODO: Add last
const TemplateGamesDataContext = createContext<GameDataProviderInterface>({
  currentPage: 1,
  pageSize: PAGE_SIZE,
  pageData: undefined,
  loading: true,
  lastPage: 1,
  allPageOptions: [] as PaginationSelectOption[],
  selectPageSize: noop,
  prev: noop,
  next: noop,
  setPage: noop,
  copyGame: noop,
  deleteGame: noop,
  importGame: noop,
  refreshGames: noop,
})

export const TemplateGamesDataProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageData, setPageData] = useState<TGameCard[]>()
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE)
  const [loading, setLoading] = useState(true)
  const [lastPage, setLastPage] = useState<number>(1)
  const [allPageOptions, setAllPageOptions] = useState<PaginationSelectOption[]>([{ value: 1, label: '1' }])
  const ownGameData = useOwnGameData()
  const { user } = React.useContext(UserContext) as UserContextType
  const [language, setLanguage] = useState<string>('en')
  const [filtersState, setFiltersState] = useState<GameFilters>({})
  const [initialFetchDone, setInitialFetchDone] = useState<boolean>(false)

  const { notifyGameImportStart, notifyGameImportFinish, notifyFetchingGamesFailed } = useGameNotifications()

  useEffect(() => {
    if (user?.language) {
      setLanguage(user.language)
    }
  }, [user])

  const next = useCallback(() => {
    if (loading) {
      return
    }

    setCurrentPage((currentState) => currentState + 1)
  }, [loading])

  const prev = useCallback(() => {
    if (loading) {
      return
    }

    setCurrentPage((currentState) => currentState - 1)
  }, [loading])

  const selectPageSize = useCallback(
    (newPageSize: number) => {
      if (loading) {
        return
      }

      setCurrentPage(1)
      setPageSize(newPageSize)
    },
    [loading],
  )

  const setPage = useCallback(
    (newPage: number) => {
      if (loading) {
        return
      }

      setCurrentPage(newPage)
    },
    [loading],
  )

  useEffect(() => {
    const abortController = new AbortController()
    setLoading(true)
    getLibraryGames({
      perPage: pageSize,
      page: currentPage,
      language,
      corporate: true,
      filters: filtersState,
    })
      .then((templateGamesResponse) => {
        if (templateGamesResponse.success) {
          setLastPage(Math.ceil(templateGamesResponse.value.count / pageSize))
          setPageData(parseLibraryGamesResponseToGameCards(templateGamesResponse.value, LibrarySource.TEMPLATE))
        } else {
          notifyFetchingGamesFailed()
          console.error(templateGamesResponse.error)
        }
      })
      .finally(() => {
        setLoading(false)
        setInitialFetchDone(true)
      })
    return () => abortController.abort()
  }, [currentPage, pageSize, language, notifyFetchingGamesFailed, filtersState])

  useEffect(() => {
    const calculateAllPageOptions = Array.from({ length: lastPage }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}`,
    }))
    setAllPageOptions(calculateAllPageOptions)
  }, [lastPage])

  const refreshGames = useCallback(() => {
    getLibraryGames({
      perPage: pageSize,
      page: currentPage,
      language,
      corporate: true,
      filters: filtersState,
    }).then((templateGamesResponse) => {
      if (templateGamesResponse.success) {
        setPageData(parseLibraryGamesResponseToGameCards(templateGamesResponse.value, LibrarySource.TEMPLATE))
        setLastPage(Math.ceil(templateGamesResponse.value.count / pageSize))
      } else {
        console.error(templateGamesResponse.error)
      }
    })
  }, [currentPage, pageSize, language, filtersState])

  const importGame = useCallback(
    async (gameId: number, gameName: string) => {
      const notifyId = notifyGameImportStart(gameName)
      const cloneResponse = await cloneLibraryGame({ gameId })
      if (cloneResponse.success) {
        ownGameData.refreshGames()
      } else {
        console.error(cloneResponse.error)
      }
      notifyGameImportFinish(notifyId, gameName, cloneResponse.success)
    },
    [notifyGameImportStart, notifyGameImportFinish, ownGameData],
  )

  const setFilters = useCallback((newFilters: GameFilters | Partial<GameFilters>, isPartial?: boolean) => {
    setFiltersState((prev) => {
      const filters = isPartial ? { ...prev, ...newFilters } : (newFilters as GameFilters)
      return areObjectsEqual(filters, prev) ? prev : filters
    })
  }, [])

  return (
    <TemplateGamesDataContext.Provider
      value={{
        currentPage,
        pageSize,
        pageData,
        lastPage,
        allPageOptions,
        loading,
        initialFetchDone,
        filters: filtersState,
        setPage,
        selectPageSize,
        next,
        prev,
        copyGame: noop,
        deleteGame: noop,
        importGame,
        refreshGames,
        setFilters,
      }}
    >
      {children}
    </TemplateGamesDataContext.Provider>
  )
}

export const useGameData = () => {
  const context = useContext(TemplateGamesDataContext)

  return context
}
